import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Users,
  Calendar,
  FileText,
  CreditCard,
  Package,
  BarChart3,
  Settings,
  Shield,
  LogOut,
  Menu,
  X,
  MessageSquare,
  Activity,
  LucideIcon,
  Wrench,
  Receipt,
  Wallet,
  Banknote,
  Landmark,
  BookText,
  CalendarClock,
  ChevronRight,
} from 'lucide-react';
import NotificationIcon from '../notifications/NotificationIcon';
import { hasRole, hasAnyRole, userRoles } from '../../utils/roles';

interface MenuItem {
  path: string;
  label: string;
  icon: LucideIcon;
  section?: string;
}

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const mainMenuItems: MenuItem[] = [
    ...(hasAnyRole(user, ['admin', 'comptable']) ? [
      { path: '/dashboard', label: 'Tableau de bord', icon: BarChart3, section: 'Général' },
    ] : []),
    { path: '/patients', label: 'Patients', icon: Users, section: 'Général' },
    { path: '/planning', label: 'Planning', icon: Calendar, section: 'Général' },
    { path: '/consultations', label: 'Consultations', icon: FileText, section: 'Clinique' },
    { path: '/prosthesis', label: 'Prothèses', icon: Wrench, section: 'Clinique' },
    { path: '/facturation', label: 'Facturation', icon: CreditCard, section: 'Finance' },
    ...(hasAnyRole(user, ['admin', 'secretary']) ? [
      { path: '/caisse', label: 'Caisse', icon: Banknote, section: 'Finance' },
    ] : []),
    ...(hasAnyRole(user, ['admin', 'secretary', 'comptable']) ? [
      { path: '/tresorerie', label: 'Trésorerie', icon: Landmark, section: 'Finance' },
    ] : []),
    ...(hasAnyRole(user, ['admin', 'comptable']) ? [
      { path: '/comptabilite', label: 'Comptabilité', icon: BookText, section: 'Finance' },
    ] : []),
    { path: '/insurance-management', label: 'Assurances', icon: Receipt, section: 'Finance' },
    { path: '/depenses', label: 'Dépenses', icon: Wallet, section: 'Finance' },
    { path: '/stock', label: 'Stock', icon: Package, section: 'Gestion' },
    { path: '/communications', label: 'Communications', icon: MessageSquare, section: 'Gestion' },
    ...(hasAnyRole(user, ['admin', 'secretary']) ? [
      { path: '/appointments/disponibilites', label: 'Disponibilités RDV', icon: CalendarClock, section: 'Gestion' },
    ] : []),
  ];

  const adminItems: MenuItem[] = hasRole(user, 'admin') ? [
    { path: '/administration', label: 'Administration', icon: Shield },
    { path: '/logs', label: 'Logs & Traçabilité', icon: Activity },
  ] : [];

  const allMenuItems = [...mainMenuItems, ...adminItems];

  const handleLogout = (): void => {
    logout();
    navigate('/login');
  };

  const getUserInitials = (): string => {
    if (user?.first_name && user?.last_name) {
      return (user.first_name.charAt(0) + user.last_name.charAt(0)).toUpperCase();
    }
    if (user?.first_name) return user.first_name.charAt(0).toUpperCase();
    if (user?.username) return user.username.charAt(0).toUpperCase();
    return 'U';
  };

  const getRoleLabel = (role: string): string => {
    const labels: Record<string, string> = {
      admin: 'Administrateur',
      dentist: 'Dentiste',
      secretary: 'Secrétaire',
      comptable: 'Comptable',
    };
    return labels[role] || role;
  };

  // Libellé combiné de tous les rôles de l'utilisateur
  const rolesLabel = userRoles(user).map(getRoleLabel).join(', ');

  const getRoleBadgeColor = (role: string): string => {
    const colors: Record<string, string> = {
      admin: 'bg-blue-500/20 text-blue-300',
      dentist: 'bg-teal-500/20 text-teal-300',
      secretary: 'bg-purple-500/20 text-purple-300',
      comptable: 'bg-amber-500/20 text-amber-300',
    };
    return colors[role] || 'bg-gray-500/20 text-gray-300';
  };

  const currentPageLabel = allMenuItems.find(item =>
    location.pathname.startsWith(item.path)
  )?.label || 'Cabinet Dentaire';

  const todayLabel = new Date().toLocaleDateString('fr-FR', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });

  const sections = ['Général', 'Clinique', 'Finance', 'Gestion'];

  const SidebarContent: React.FC = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="sidebar-logo-area flex items-center justify-between h-16 px-4">
        <div className="flex items-center gap-3">
          <div className="bg-white rounded-xl px-3 py-1.5">
            <img
              src="/Logo - SONRISA.png"
              alt="Sonrisa"
              className="h-16 w-auto"
              style={{ objectFit: 'contain' }}
            />
          </div>
        </div>
        <button
          type="button"
          className="lg:hidden text-white/50 hover:text-white transition-colors"
          onClick={() => setSidebarOpen(false)}
          aria-label="Fermer le menu"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 scrollbar-thin">
        {sections.map((section) => {
          const items = mainMenuItems.filter(item => item.section === section);
          if (items.length === 0) return null;
          return (
            <div key={section}>
              <p className="sidebar-section-label">{section}</p>
              {items.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname.startsWith(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`sidebar-nav-item mb-0.5 ${isActive ? 'active' : ''}`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <Icon className={`h-4 w-4 flex-shrink-0 sidebar-icon ${isActive ? 'text-teal-400' : ''}`} />
                    <span className="flex-1 text-[13px]">{item.label}</span>
                    {isActive && <ChevronRight className="h-3 w-3 opacity-60" />}
                  </Link>
                );
              })}
            </div>
          );
        })}

        {/* Admin section */}
        {adminItems.length > 0 && (
          <div>
            <p className="sidebar-section-label">Administration</p>
            {adminItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname.startsWith(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`sidebar-nav-item mb-0.5 ${isActive ? 'active' : ''}`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon className={`h-4 w-4 flex-shrink-0 ${isActive ? 'text-teal-400' : ''}`} />
                  <span className="flex-1 text-[13px]">{item.label}</span>
                  {isActive && <ChevronRight className="h-3 w-3 opacity-60" />}
                </Link>
              );
            })}
          </div>
        )}
      </nav>

      {/* User area */}
      <div className="sidebar-user-area p-4">
        <div className="flex items-center gap-3 mb-3">
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
            style={{ background: 'var(--grad-teal)' }}
          >
            {getUserInitials()}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-white truncate">
              {user?.first_name} {user?.last_name}
            </p>
            <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${getRoleBadgeColor(user?.role || '')}`}>
              {rolesLabel}
            </span>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 w-full px-3 py-2 text-xs font-medium rounded-xl transition-all duration-200"
          style={{ color: 'rgba(255,255,255,0.5)' }}
          onMouseEnter={e => {
            (e.currentTarget as HTMLButtonElement).style.background = 'rgba(239,68,68,0.12)';
            (e.currentTarget as HTMLButtonElement).style.color = '#FCA5A5';
          }}
          onMouseLeave={e => {
            (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
            (e.currentTarget as HTMLButtonElement).style.color = 'rgba(255,255,255,0.5)';
          }}
        >
          <LogOut className="h-3.5 w-3.5" />
          Se déconnecter
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: 'var(--page-bg)' }}>

      {/* ── Sidebar desktop ── */}
      <aside
        className="sidebar-sonrisa hidden lg:flex lg:flex-col w-64 flex-shrink-0"
        style={{ minHeight: '100vh' }}
      >
        <SidebarContent />
      </aside>

      {/* ── Sidebar mobile (drawer) ── */}
      <div
        className={`fixed inset-0 z-50 lg:hidden transition-opacity duration-300 ${sidebarOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
          }`}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0"
          style={{ background: 'rgba(13,31,53,0.6)', backdropFilter: 'blur(4px)' }}
          onClick={() => setSidebarOpen(false)}
        />
        {/* Drawer */}
        <aside
          className={`sidebar-sonrisa absolute inset-y-0 left-0 w-72 flex flex-col transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
            }`}
        >
          <SidebarContent />
        </aside>
      </div>

      {/* ── Main content ── */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">

        {/* Header */}
        <header className="app-header h-[70px] flex items-center px-5 sm:px-6 gap-4 flex-shrink-0 sticky top-0 z-30">
          {/* Mobile menu trigger */}
          <button
            className="lg:hidden flex items-center justify-center w-8 h-8 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-colors"
            onClick={() => setSidebarOpen(true)}
            aria-label="Ouvrir le menu"
          >
            <Menu className="h-5 w-5" />
          </button>

          {/* Page title + subtitle */}
          <div className="flex-1 min-w-0">
            <h1 className="app-header-title text-[18px] font-extrabold truncate leading-tight">{currentPageLabel}</h1>
            <p className="text-[12.5px] text-gray-500 mt-0.5 truncate capitalize">{todayLabel}</p>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <NotificationIcon />
            {/* User avatar */}
            <div className="hidden md:flex items-center gap-2.5 pl-2 border-l border-gray-100">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold"
                style={{ background: 'var(--grad-blue)' }}
              >
                {getUserInitials()}
              </div>
              <div className="text-right hidden lg:block">
                <p className="text-xs font-semibold text-gray-800 leading-tight">
                  {user?.first_name} {user?.last_name}
                </p>
                <p className="text-[11px] text-gray-500 leading-tight">
                  {rolesLabel}
                </p>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="son-ui flex-1 overflow-y-auto p-5 lg:p-6 animate-fade-in">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
